import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { KeyRound, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { Button, Input, Card, CardHeader, CardTitle, CardContent } from '@nzassa/ui';
import { COLORS } from '@nzassa/shared';
import type { UserRole } from '@nzassa/shared';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { getDefaultRoute } from '@/lib/roleRedirect';

/** Demo seed matricules — CICN Bingerville 2026 */
const DEMO_MATRICULES: Partial<Record<UserRole, string>> = {
  SUPER_USER_ADMIN: 'SUD-001',
  ADMIN_ANSUT: 'AAN-001',
  ADMIN_COLLEGE: 'ACO-BIN-01',
  FORMATEUR_PRINCIPAL: 'FPR-BIN-01',
  FORMATEUR_ASSISTANT: 'FAS-BIN-01',
  APPRENANT: 'APP-BIN-001',
};

export function AuthPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('auth_legacy.connexion'));
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  // Login form state
  const [matricule, setMatricule] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // First login state
  const [firstLogin, setFirstLogin] = useState(false);
  const [firstLoginMatricule, setFirstLoginMatricule] = useState('');
  const [firstLoginName, setFirstLoginName] = useState('');
  const [firstLoginOldPassword, setFirstLoginOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const canSubmit = matricule.trim().length >= 3 && password.length >= 1;

  const handleLogin = async (demoMatricule?: string) => {
    setLoading(true);
    setError('');

    const mat = demoMatricule || matricule.trim();
    const pwd = demoMatricule ? 'Demo2026!' : password; // CICN demo accounts use Demo2026!

    try {
      const response = await api.login(mat, pwd);

      if (response.requiresFirstLogin) {
        setFirstLogin(true);
        setFirstLoginMatricule(response.matricule ?? mat);
        setFirstLoginName(response.userName ?? '');
        setFirstLoginOldPassword(pwd); // Save temp password for first-login verification
        return;
      }

      if (response.token && response.user) {
        login(response.token, response.user);
        navigate(getDefaultRoute(response.user.role));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('errors.erreur_survenue');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleFirstLogin = async () => {
    if (newPassword.length < 6) {
      setError(t('auth_legacy.mdp_6_caracteres'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth_legacy.mdp_different'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.firstLogin(firstLoginMatricule, newPassword, confirmPassword, firstLoginOldPassword);

      if (response.token && response.user) {
        login(response.token, response.user);
        navigate(getDefaultRoute(response.user.role));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('errors.erreur_survenue');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // First login screen
  if (firstLogin) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-lg"
        >
          <Card className="shadow-2xl shadow-slate-200/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-2xl">
                <div className="p-2 rounded-full bg-green-50">
                  <ShieldCheck className="h-6 w-6" style={{ color: '#16A34A' }} />
                </div>
                {t('auth_legacy.premiere_connexion')}
              </CardTitle>
              <div className="mt-2 text-sm text-slate-600 pl-1">
                {t('auth_legacy.bienvenue_nom', { name: firstLoginName })}
              </div>
            </CardHeader>
            <CardContent className="pt-2">
              <form onSubmit={(e) => { e.preventDefault(); handleFirstLogin(); }} className="space-y-4">
              <div className="text-xs text-slate-500">
                {t('auth.matricule')} : <span className="font-mono font-semibold">{firstLoginMatricule}</span>
              </div>

              <div className="space-y-2">
                <label htmlFor="auth-new-password" className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  {t('auth_legacy.nouveau_mdp')}
                </label>
                <div className="relative">
                  <Input
                    id="auth-new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder={t('auth_legacy.placeholder_mdp')}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showPassword ? t('auth.masquer_mdp') : t('auth.afficher_mdp')}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="auth-confirm-password" className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  {t('auth_legacy.confirmer_mdp')}
                </label>
                <Input
                  id="auth-confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('auth_legacy.retaper_mdp')}
                />
              </div>

              {error && (
                <div role="alert" className="rounded-xl bg-red-50 border border-red-100 p-3 text-xs text-red-600">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setFirstLogin(false); setError(''); }}
                  className="flex-1"
                >
                  {t('common.retour')}
                </Button>
                <Button
                  type="submit"
                  disabled={!newPassword || !confirmPassword || loading}
                  className="flex-[2]"
                  variant="secondary"
                >
                  {loading ? t('auth_legacy.creation_en_cours') : t('auth_legacy.creer_mdp')}
                </Button>
              </div>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Login screen
  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-lg"
      >
        <Card className="shadow-2xl shadow-slate-200/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-2xl">
              <div className="p-2 rounded-full bg-orange-50">
                <KeyRound className="h-6 w-6" style={{ color: COLORS.orange }} />
              </div>
              {t('auth_legacy.connexion')}
            </CardTitle>
            <div className="mt-2 text-sm text-slate-600 pl-1">
              {t('auth_legacy.sous_titre')}
            </div>
          </CardHeader>
          <CardContent className="space-y-6 pt-2">
            <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) handleLogin(); }} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="auth-matricule" className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  {t('auth.matricule')}
                </label>
                <Input
                  id="auth-matricule"
                  value={matricule}
                  onChange={(e) => setMatricule(e.target.value.toUpperCase())}
                  placeholder={t('auth_legacy.placeholder_matricule')}
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="auth-password" className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  {t('auth.mot_de_passe')}
                </label>
                <div className="relative">
                  <Input
                    id="auth-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder={t('auth.ton_mot_de_passe')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showPassword ? t('auth.masquer_mdp') : t('auth.afficher_mdp')}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="text-xs text-slate-400">
                  {t('auth_legacy.premiere_connexion_hint')}
                </div>
              </div>

              {error && (
                <div role="alert" className="rounded-xl bg-red-50 border border-red-100 p-3 text-xs text-red-600">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/')}
                  className="flex-1"
                >
                  {t('common.annuler')}
                </Button>
                <Button
                  type="submit"
                  disabled={!canSubmit || loading}
                  className="flex-[2]"
                  variant="secondary"
                >
                  {loading ? t('auth_legacy.connexion_en_cours') : t('auth_legacy.se_connecter')}
                </Button>
              </div>

              <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-4 text-xs text-blue-800">
                <span className="font-semibold">Note :</span> {t('auth_legacy.note_matricule')}
              </div>
            </form>

            {/* Demo quick-access links */}
            <div className="pt-6 mt-2 border-t border-slate-100 space-y-2">
              <div className="text-center text-xs text-slate-400 mb-3">{t('auth_legacy.acces_rapide_demo')}</div>
              <div className="flex flex-wrap justify-center gap-2">
                {(
                  [
                    ['SUPER_USER_ADMIN', 'Super Admin'],
                    ['ADMIN_ANSUT', 'Admin ANSUT'],
                    ['FORMATEUR_PRINCIPAL', 'Formateur'],
                    ['APPRENANT', 'Apprenant'],
                  ] as [UserRole, string][]
                ).map(([role, label]) => (
                  <button
                    key={role}
                    onClick={() => handleLogin(DEMO_MATRICULES[role])}
                    disabled={loading}
                    className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors px-2 py-1 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                  >
                    <ShieldCheck className="h-3 w-3" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
